import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, model, NgZone, OnDestroy, OnInit, signal, untracked, ViewChild } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventApi, EventContentArg } from '@fullcalendar/core';
import { IconsService } from 'src/app/shared/services/icons.service';
import { CommonModule } from '@angular/common';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CALENDAR_EVENT_TYPE_GROUP_KEYS_BY_SETTLEMENT_TYPE, CALENDAR_EVENT_TYPE_GROUPS, CALENDAR_VIEWS, CalendarEventTypeGroupKey } from 'src/app/shared/constants/zazz-calendar';
import { SettlementsService } from 'src/app/services/settlements.service';
import { EVENTS_BG_PALLETE, EVENTS_FONT_PALLETE } from 'src/app/shared/constants/events-pallete';
import { STORAGE_KEYS, StorageService } from 'src/app/services/storage.service';
import { Settlement } from 'src/app/models/settlement.model';
import { InfiniteSelectSettlementsModule } from '../inputs/infinite-select/infinite-select-settlements.module';
import { InfiniteSelectComponent } from '../inputs/infinite-select/infinite-select.component';
import { CALENDAR_FILTER_KEYS, CalendarParams } from 'src/app/models/calendar-params.interface';
import { CALENDAR_EVENT_STATUS_NAMES, CalendarEventStatus, CalendarEventStatusName, CalendarEventType, EventMapItem, EVENTS_NAME_DESCRIPTION } from 'src/app/models/calendar-event.interface';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { of, Subscription } from 'rxjs';
import {
  CalendarEventStatusInteractionState,
  getCalendarEventManualStatusOptions,
  getCalendarEventStatusInteractionState,
  isManuallySelectableCalendarEventStatus,
  isTerminalCalendarEventStatus
} from 'src/app/shared/utils/calendar-event-status.utils';
import {
  CalendarEventSelectionCandidate,
  CalendarEventSelectionGroup,
  CalendarEventSelectionIdentity,
  CalendarEventSelectionMismatch,
  getCalendarEventSelectionGroup,
  getCalendarEventSelectionMismatch,
  isValidCalendarEventSelection
} from 'src/app/shared/utils/calendar-event-selection.utils';

interface VisibleCalendarEventTypeGroup {
  key: CalendarEventTypeGroupKey;
  label: string;
  events: CalendarEventType[];
}

interface CalendarEventSelectionGroupView extends CalendarEventSelectionGroup {
  allSelected: boolean;
}

@Component({
  selector: 'app-zazz-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FullCalendarModule,
    FontAwesomeModule,
    InfiniteSelectSettlementsModule,
    NgbTooltipModule
],
  templateUrl: './zazz-calendar.component.html',
  styleUrl: './zazz-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZazzCalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  public filterKeys = {...CALENDAR_FILTER_KEYS};
  private readonly SETTLEMENTS_SOURCE_ID = 'settlements';
  private readonly clockSvgHtml = icon(faClock, { classes: ['event-clock-icon'] }).html.join('');
  private tooltipEl: HTMLDivElement | null = null;
  private tooltipHideHandle: ReturnType<typeof setTimeout> | null = null;
  private tooltipsEnabled = true;
  private batchUpdateSubscription?: Subscription;
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  @ViewChild(FullCalendarComponent, {static: true}) calendar!: FullCalendarComponent;
  @ViewChild(InfiniteSelectComponent, { static: true }) private settlementSelect?: InfiniteSelectComponent;
  section = input<string>('dashboard');
  store = inject(SettlementsService);
  private zone = inject(NgZone);
  private toastr = inject(ToastrService);
  storageService = inject(StorageService);
  isLoading = signal(false);
  currentProfile = input.required<any>();
  profile: any = {};
  views = CALENDAR_VIEWS[this.section()];
  selectedView = 'dayGridMonth';
  eventStatuses = signal<CalendarEventStatus[]>([]);
  updatingEventIds = signal<ReadonlySet<number>>(new Set<number>());
  selectedEventIds = signal<ReadonlySet<number>>(new Set<number>());
  selectionAnchor = signal<CalendarEventSelectionIdentity | null>(null);
  batchStatusId = signal<number | null>(null);
  isBatchUpdating = signal(false);
  batchCompletedCount = signal(0);
  batchTotalCount = signal(0);
  batchProgressPercent = computed(() => {
    const total = this.batchTotalCount();
    return total > 0
      ? Math.round((this.batchCompletedCount() / total) * 100)
      : 0;
  });
  selectedCount = computed(() => this.selectedEventIds().size);
  selectionGroup = computed<CalendarEventSelectionGroupView | null>(() => {
    const anchor = this.selectionAnchor();
    const selectedIds = this.selectedEventIds();
    if (!anchor) return null;

    const calendarApi = this.calendar?.getApi();
    if (!calendarApi) return null;

    const candidates = calendarApi.getEvents()
      .map(event => this.getEventSelectionCandidate(event))
      .filter((candidate): candidate is CalendarEventSelectionCandidate => candidate !== null);
    const group = getCalendarEventSelectionGroup(anchor, candidates);
    if (!group) return null;

    return {
      ...group,
      allSelected: group.eventIds.every(eventId => selectedIds.has(eventId))
    };
  });
  batchStatusOptions = computed(() =>
    getCalendarEventManualStatusOptions(this.eventStatuses())
  );
  public icons = inject(IconsService);
  public calendarOptions: CalendarOptions = {
    loading: (isLoading) => {
      try {
        this.zone.run(() => {
          this.isLoading.set(isLoading);
          if (isLoading) {
            setTimeout(() => { }, 0);
          }
        });
      } catch (error) {
        console.log('no loading in calendar');
      }
    },
    height: 'auto',
    views: {
      timeGridWeek: {
        expandRows: true,
        
      },
      timeGridDay: {
        expandRows: true,
      }
    },
    events: (info, successCallback, failureCallback) => {
      const utilityId = this.getActiveUtilityId();
      if (!utilityId) {
        successCallback([]);
        return;
      }

      const params = this.buildCalendarParams(
        info.startStr.split('T')[0],
        info.endStr.split('T')[0],
        utilityId
      );
    
      this.store.getCalendarEvents(params)
        .subscribe({
          next: ({ data: events }) => {
            const eventsMapByDate = new Map<string, EventMapItem[]>();
            events.forEach(e => {
              if (!eventsMapByDate.has(e.event_date)) {
                eventsMapByDate.set(e.event_date, []);
              }
              eventsMapByDate.get(e.event_date)!.push({
                id: e.id,
                settlement: e.settlement,
                unit_id: e.unit.id,
                date: e.event_date,
                description: EVENTS_NAME_DESCRIPTION[e.event.name],
                eventTypeId: e.event.id,
                eventName: e.event.name,
                eventStatus: e.event_status,
                unit_name: e.unit.name
              });
            });

            const calendarEvents: any[] = [];

            eventsMapByDate.forEach((events, date) => {
              let hour = 9;
              events.forEach(event => {
                const timeString = `${hour.toString().padStart(2, '0')}:00:00`;
                const fullDateTime = `${date}T${timeString}`;
                const startTimeLabel = `${hour.toString().padStart(2, '0')}:00`;
                const endTimeLabel = `${(hour + 1).toString().padStart(2, '0')}:00`;
                const labels = this.buildSettlementLabels(event);

                calendarEvents.push({
                    id: `${event.id}`,
                    title: labels.title,
                    start: fullDateTime,
                    end: `${date}T${(hour + 1).toString().padStart(2, '0')}:00:00`, // One hour duration
                    backgroundColor: EVENTS_BG_PALLETE[event.eventName as keyof typeof EVENTS_BG_PALLETE],
                    borderColor: EVENTS_BG_PALLETE[event.eventName as keyof typeof EVENTS_BG_PALLETE], 
                    textColor: EVENTS_FONT_PALLETE[event.eventName as keyof typeof EVENTS_FONT_PALLETE],
                    description: event.description,
                    extendedProps: {
                      eventname: event.eventName,
                      isSettlementEvent: true,
                      settlementId: event.settlement.id,
                      settlementName: event.settlement?.name ?? '',
                      unitId: event.unit_id,
                      description: event.settlement || 'Sin descripción',
                      primaryLabel: labels.primaryLabel,
                      secondaryLabel: labels.secondaryLabel,
                      settlementLabel: labels.settlementLabel,
                      unitLabel: labels.unitLabel,
                      eventTypeLabel: labels.eventTypeLabel,
                      startTimeLabel,
                      endTimeLabel,
                      calendarEventId: event.id,
                      eventDate: event.date,
                      eventTypeId: event.eventTypeId,
                      eventStatus: event.eventStatus
                    }
                  });
                  hour = (hour + 1) % 18;
                  if (hour < 9) hour = 9; // Don't go before business hours
              });
            });

            const result = successCallback(calendarEvents);
            return result;
          },
          error: (err) => {
            return failureCallback(err);
          }
        });
    },
    eventDisplay: 'block',
    eventContent: (arg) => this.renderEventContent(arg),
    eventMouseEnter: (arg: any) => {
      if ((arg.jsEvent?.target as HTMLElement | null)?.closest('.event-status-select, .event-selection-control')) return;
      if (!this.tooltipsEnabled) return;
      this.showTooltip(arg.el as HTMLElement, arg.event.title ?? '');
    },
    eventMouseLeave: () => {
      this.hideTooltip();
    },
    lazyFetching: true,
    // dayMaxEvents: true,
    allDaySlot: false,
    locale: 'es',
    selectable: true,
    nowIndicator: true,
    displayEventTime: true,
    eventTimeFormat: {
      hour: 'numeric',
      hour12: true,
      minute: '2-digit'
    },
    firstDay: 1,
    businessHours: {
      daysOfWeek: [1, 2, 3, 4, 5, 6],
      startTime: '9:00',
      endTime: '18:00',
    },
    headerToolbar: false,
    initialView: 'dayGridMonth',
    plugins: [timeGridPlugin, dayGridPlugin],
    slotLabelFormat: {
        hour: 'numeric',
        hour12: true,
    },
    contentHeight: 'auto',
    slotMinTime: '09:00',
    slotMaxTime: '18:00',
    expandRows: true,
    dayHeaderContent: (arg) => {
      const today = new Date();
      const isToday = this.isSameDay(arg.date, today);
      const className = isToday ? ' day-custom-active' : '';
      return {
        html: `
        <div class="calendar-day-header-custom${className}">
          <span>${arg.date.toLocaleDateString('es-ES', { weekday: 'short', timeZone: 'UTC' })}</span>
          <span>${ this.selectedView === 'dayGridMonth' ? '' : arg.date.toLocaleDateString('es-ES', { day: '2-digit' })}</span>
        </div>`
      }
    },
    

    dayMaxEventRows: false,
    //datesSet: (arg: DatesSetArg) => this.handleDateChange(arg)
  }

  // Calendar filters
  settlementsFetcher = (params: any) => {
    const utilityId = this.getActiveUtilityId();
    if (!utilityId) {
      return of({ data: [], meta: { current_page: 1, total_entries: 0, per_page: params.per_page } });
    }
    const payload: any = {
      page: params.page,
      per_page: params.per_page,
      utility_id: utilityId
    };
    if (this.settlementTypeValid()) {
      payload.tipo = this.settlementType();
    }
    if (params.search) {
      payload.name = params.search;
    }
    return this.store.getSettlements(payload);
  };

  settlementsPageSize = 10;
  seededSettlements: Settlement[] = [];
  selectedSettlement = model<any>(null);
  settlementType = model<number | null>(null);
  unitStatus = model<number | null>(null);
  eventType = model<number | null>(null);
  municipality = model<string>('');
  calendarEventTypes = signal<CalendarEventType[]>([]);
  calendarEventTypeGroups = computed<VisibleCalendarEventTypeGroup[]>(() => {
    const eventTypesByName = new Map(
      this.calendarEventTypes().map((eventType) => [eventType.name, eventType])
    );
    const visibleGroupKeys = this.getVisibleEventTypeGroupKeys(this.settlementType());

    return CALENDAR_EVENT_TYPE_GROUPS
      .filter((group) => visibleGroupKeys.includes(group.key))
      .map((group) => ({
        key: group.key,
        label: group.label,
        events: group.eventNames
          .map((eventName) => eventTypesByName.get(eventName))
          .filter((eventType): eventType is CalendarEventType => Boolean(eventType))
      }))
      .filter((group) => group.events.length > 0);
  });

  constructor() {
    effect(() => {
      const section = this.section();
      this.views = CALENDAR_VIEWS[section];
    });
    effect(() => {
      const profile = this.currentProfile();
      untracked(() => this.refreshForProfile(profile));
    });
    effect(() => {
      const settlementType = this.settlementType();
      this.selectedSettlement.set(null);
    },
    {
      allowSignalWrites: true
    });
    effect(() => {
      const selectedEventType = this.eventType();
      if (!selectedEventType) {
        return;
      }

      const isSelectedEventTypeVisible = this.calendarEventTypeGroups().some((group) =>
        group.events.some((eventType) => eventType.id === selectedEventType)
      );

      if (!isSelectedEventTypeVisible) {
        this.eventType.set(null);
      }
    }, {
      allowSignalWrites: true
    });
    this.getEventTypes();
  }

  ngOnInit(): void {
    this.profile = this.storageService.getItem(STORAGE_KEYS.PROFILE) ? this.storageService.getItem(STORAGE_KEYS.PROFILE).profile : null;
    this.getEventStatuses();
  }

  ngAfterViewInit(): void {
    this.syncTooltipsForView(this.selectedView);
  }

  ngOnDestroy(): void {
    this.batchUpdateSubscription?.unsubscribe();
    this.disposeTooltipEl();
  }

  settlementTypeValid(isHTML: boolean = false): boolean {
    const list = isHTML ? [0, 1, 2 ] : [0, 1];
    return list.some(e => e === this.settlementType());
  }

  getEventTypes() {
    this.store.getCalendarEventTypes().subscribe((data) => this.calendarEventTypes.set(data ?? []));
  }

  getEventStatuses() {
    this.store.getCalendarEventStatuses().subscribe({
      next: (statuses) => {
        const expectedStatuses = CALENDAR_EVENT_STATUS_NAMES
          .map(name => statuses.find(status => status.name === name))
          .filter((status): status is CalendarEventStatus => Boolean(status));
        this.eventStatuses.set(expectedStatuses);
        this.calendar?.getApi()?.refetchEvents();
      },
      error: () => {
        this.eventStatuses.set([]);
        this.toastr.error('No fue posible cargar el catálogo de estados.', 'Calendario');
      }
    });
  }

  selectSettlement(eventOrValue: any) {
    const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    this.selectedSettlement.set(value ?? null);  
  }

  private getVisibleEventTypeGroupKeys(settlementType: number | null): CalendarEventTypeGroupKey[] {
    if (settlementType === 0 || settlementType === 1 || settlementType === 2) {
      return CALENDAR_EVENT_TYPE_GROUP_KEYS_BY_SETTLEMENT_TYPE[settlementType];
    }

    return CALENDAR_EVENT_TYPE_GROUP_KEYS_BY_SETTLEMENT_TYPE[2];
  }

  dateChange(type: 'prev' | 'next') {
  if (this.isBatchUpdating()) return;
  this.clearBatchSelection(false);
  //this.zone.run(() => this.isLoading.set(true));
  setTimeout(() => {
    const calendarApi = this.calendar.getApi();
    type === 'prev' ? calendarApi.prev() : calendarApi.next();
  }, 0);
}

  get currentDate() {
    return this.calendar?.getApi()?.getDate()?.toLocaleDateString('es-Es', { month: 'long', year: 'numeric' }) ?? '';
  }

  changeView() {
  if (this.isBatchUpdating()) return;
  this.clearBatchSelection(false);
  const api = this.calendar.getApi();
  requestAnimationFrame(() => {
    this.syncTooltipsForView(this.selectedView);
    api.changeView(this.selectedView);
  });
}

  private syncTooltipsForView(view: string) {
    const shouldEnable = ['dayGridMonth', 'timeGridWeek', 'timeGridDay'].includes(view);
    this.tooltipsEnabled = shouldEnable;
    if (shouldEnable) {
      this.ensureTooltipEl();
    } else {
      this.hideTooltip();
    }
  }

  private ensureTooltipEl() {
    if (this.tooltipEl) return;
    this.tooltipEl = document.createElement('div');
    this.tooltipEl.className = 'zazz-fc-tooltip';
    this.tooltipEl.style.display = 'none';
    document.body.appendChild(this.tooltipEl);
  }

  private disposeTooltipEl() {
    if (!this.tooltipEl) return;
    if (this.tooltipHideHandle) {
      clearTimeout(this.tooltipHideHandle);
      this.tooltipHideHandle = null;
    }
    this.tooltipEl.remove();
    this.tooltipEl = null;
  }

  private showTooltip(target: HTMLElement, text: string) {
    if (!text) return;
    this.ensureTooltipEl();
    if (!this.tooltipEl) return;
    if (this.tooltipHideHandle) {
      clearTimeout(this.tooltipHideHandle);
      this.tooltipHideHandle = null;
    }
    this.tooltipEl.textContent = text;
    this.tooltipEl.style.display = 'block';
    this.tooltipEl.style.visibility = 'hidden';
    this.tooltipEl.classList.remove('zazz-fc-tooltip--bottom');
    this.tooltipEl.classList.remove('is-visible');
    this.positionTooltip(target);
    this.tooltipEl.style.visibility = 'visible';
    requestAnimationFrame(() => {
      this.tooltipEl?.classList.add('is-visible');
    });
  }

  private hideTooltip() {
    if (!this.tooltipEl) return;
    this.tooltipEl.classList.remove('is-visible');
    this.tooltipHideHandle = setTimeout(() => {
      if (!this.tooltipEl) return;
      this.tooltipEl.style.display = 'none';
    }, 120);
  }

  private positionTooltip(target: HTMLElement) {
    if (!this.tooltipEl) return;
    const rect = target.getBoundingClientRect();
    const tooltipRect = this.tooltipEl.getBoundingClientRect();
    const padding = 8;
    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));
    let top = rect.top - tooltipRect.height - 8;
    if (top < padding) {
      top = rect.bottom + 8;
      this.tooltipEl.classList.add('zazz-fc-tooltip--bottom');
    }
    this.tooltipEl.style.left = `${Math.round(left)}px`;
    this.tooltipEl.style.top = `${Math.round(top)}px`;
  }

  private renderEventContent(arg: EventContentArg): { domNodes: HTMLElement[] } {
    const rawPrimary = (arg.event.extendedProps['primaryLabel'] ?? arg.event.title ?? '').trim();
    const rawSecondary = (arg.event.extendedProps['secondaryLabel'] ?? '').trim();
    const structuredLabels = [
      {
        className: 'event-settlement',
        value: String(arg.event.extendedProps['settlementLabel'] ?? '').trim()
      },
      {
        className: 'event-unit',
        value: String(arg.event.extendedProps['unitLabel'] ?? '').trim()
      },
      {
        className: 'event-type',
        value: String(arg.event.extendedProps['eventTypeLabel'] ?? '').trim()
      }
    ].filter(label => Boolean(label.value));
    const startLabel = arg.event.extendedProps['startTimeLabel'] ?? '';
    const endLabel = arg.event.extendedProps['endTimeLabel'] ?? '';
    const timeLabel = endLabel ? `${startLabel} - ${endLabel}` : startLabel;
    const currentStatus = arg.event.extendedProps['eventStatus'] as CalendarEventStatus | undefined;
    const calendarEventId = Number(arg.event.extendedProps['calendarEventId']);

    const container = document.createElement('div');
    container.className = structuredLabels.length
      ? 'event-item-container has-structured-labels'
      : `event-item-container${rawSecondary ? ' has-subtitle' : ''}`;

    const header = document.createElement('div');
    header.className = 'event-header';
    header.appendChild(this.createEventSelectionControl(
      arg.event,
      calendarEventId,
      currentStatus,
      arg.event.title || rawPrimary
    ));

    const textContainer = document.createElement('div');
    textContainer.className = 'event-text';

    if (structuredLabels.length) {
      structuredLabels.forEach(labelData => {
        const label = document.createElement('span');
        label.className = `event-label ${labelData.className}`;
        label.textContent = labelData.value;
        textContainer.appendChild(label);
      });
    } else {
      const title = document.createElement('span');
      title.className = 'event-title';
      title.textContent = rawPrimary;
      textContainer.appendChild(title);

      if (rawSecondary) {
        const subtitle = document.createElement('span');
        subtitle.className = 'event-subtitle';
        subtitle.textContent = rawSecondary;
        textContainer.appendChild(subtitle);
      }
    }
    header.appendChild(textContainer);

    const footer = document.createElement('div');
    footer.className = 'event-footer';

    const timing = document.createElement('div');
    timing.className = 'timing';
    timing.innerHTML = this.clockSvgHtml;
    const time = document.createElement('span');
    time.textContent = timeLabel;
    timing.appendChild(time);
    footer.appendChild(timing);

    const statusSelect = this.createEventStatusSelect(
      arg.event,
      calendarEventId,
      currentStatus,
      arg.event.title || rawPrimary
    );
    footer.appendChild(statusSelect);

    container.append(header, footer);
    return { domNodes: [container] };
  }

  private createEventSelectionControl(
    calendarEvent: EventApi,
    calendarEventId: number,
    currentStatus: CalendarEventStatus | undefined,
    eventLabel: string
  ): HTMLLabelElement {
    const control = document.createElement('label');
    control.className = 'event-selection-control';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'event-selection-checkbox';
    checkbox.dataset['calendarEventId'] = `${calendarEventId}`;
    checkbox.checked = this.selectedEventIds().has(calendarEventId);
    checkbox.setAttribute('aria-label', `Seleccionar ${eventLabel} para actualización por lote`);

    const identity = this.getEventSelectionIdentity(calendarEvent);
    const disabledReason = this.getEventSelectionDisabledReason(identity, currentStatus);
    checkbox.disabled = disabledReason !== null;
    control.title = disabledReason ?? 'Seleccionar tarea para actualización por lote';

    const visibleLabel = document.createElement('span');
    visibleLabel.textContent = 'Seleccionar';
    control.append(checkbox, visibleLabel);

    ['click', 'mousedown', 'pointerdown'].forEach(eventName => {
      control.addEventListener(eventName, event => {
        event.stopPropagation();
        this.hideTooltip();
      });
    });

    checkbox.addEventListener('change', event => {
      event.stopPropagation();
      if (!identity) {
        checkbox.checked = false;
        return;
      }

      this.zone.run(() => this.toggleEventSelection(identity, checkbox.checked));
    });

    return control;
  }

  private createEventStatusSelect(
    calendarEvent: EventApi,
    calendarEventId: number,
    currentStatus: CalendarEventStatus | undefined,
    eventLabel: string
  ): HTMLSelectElement {
    const select = document.createElement('select');
    select.className = 'event-status-select form-select form-select-sm';
    select.dataset['calendarEventId'] = `${calendarEventId}`;
    select.setAttribute('aria-label', `Estado de ${eventLabel}`);

    const availableStatuses = getCalendarEventManualStatusOptions(
      this.eventStatuses(),
      currentStatus
    );

    availableStatuses.forEach(status => {
      const option = document.createElement('option');
      option.value = `${status.id}`;
      option.textContent = status.description || status.name;
      option.selected = status.id === currentStatus?.id;
      option.disabled = !isManuallySelectableCalendarEventStatus(status.name);
      if (option.disabled) {
        option.title = 'Estado asignado automáticamente';
      }
      select.appendChild(option);
    });

    const interactionState = getCalendarEventStatusInteractionState({
      statusName: currentStatus?.name,
      hasCurrentStatus: Boolean(currentStatus),
      hasValidEventId: Number.isFinite(calendarEventId),
      hasCompleteCatalog: this.eventStatuses().length === CALENDAR_EVENT_STATUS_NAMES.length,
      isUpdating: this.updatingEventIds().has(calendarEventId)
    });
    this.applyStatusAppearance(select, currentStatus?.name);
    this.applyStatusInteractionState(select, interactionState);
    if (this.selectedEventIds().has(calendarEventId)) {
      select.dataset['interactionState'] = 'unavailable';
      select.disabled = true;
      select.title = 'La tarea está seleccionada para actualización por lote';
    }

    ['click', 'mousedown', 'pointerdown'].forEach(eventName => {
      select.addEventListener(eventName, event => {
        event.stopPropagation();
        this.hideTooltip();
      });
    });

    select.addEventListener('change', event => {
      event.stopPropagation();
      if (isTerminalCalendarEventStatus(currentStatus?.name)) {
        select.value = `${currentStatus?.id ?? ''}`;
        this.applyStatusInteractionState(select, 'locked');
        return;
      }

      const nextStatusId = Number(select.value);
      const nextStatus = this.eventStatuses().find(status => status.id === nextStatusId);
      if (!nextStatus
        || !isManuallySelectableCalendarEventStatus(nextStatus.name)
        || !currentStatus
        || nextStatus.id === currentStatus.id) return;

      this.applyStatusAppearance(select, nextStatus.name);
      this.applyStatusInteractionState(select, 'updating');
      this.zone.run(() => this.updateEventStatus(calendarEvent, select, currentStatus, nextStatus));
    });

    return select;
  }

  private updateEventStatus(
    calendarEvent: EventApi,
    select: HTMLSelectElement,
    previousStatus: CalendarEventStatus,
    nextStatus: CalendarEventStatus
  ) {
    const calendarEventId = Number(calendarEvent.extendedProps['calendarEventId']);
    this.setEventUpdating(calendarEventId, true);

    this.store.updateCalendarEventStatus(calendarEventId, nextStatus.id).subscribe({
      next: (updatedEvent) => {
        const updatedStatus: CalendarEventStatus = {
          ...nextStatus,
          ...(updatedEvent?.event_status ?? {}),
          description: updatedEvent?.event_status?.description ?? nextStatus.description
        };
        this.setEventUpdating(calendarEventId, false);
        select.value = `${updatedStatus.id}`;
        this.applyStatusAppearance(select, updatedStatus.name);
        this.applyStatusInteractionState(
          select,
          isTerminalCalendarEventStatus(updatedStatus.name) ? 'locked' : 'editable'
        );
        calendarEvent.setExtendedProp('eventStatus', updatedStatus);
        this.toastr.success(`Estado actualizado a ${updatedStatus.description ?? updatedStatus.name}.`, 'Calendario');
      },
      error: () => {
        this.setEventUpdating(calendarEventId, false);
        select.value = `${previousStatus.id}`;
        this.applyStatusAppearance(select, previousStatus.name);
        this.applyStatusInteractionState(
          select,
          isTerminalCalendarEventStatus(previousStatus.name) ? 'locked' : 'editable'
        );
        this.toastr.error('No fue posible actualizar el estado. Se restauró el valor anterior.', 'Calendario');
      }
    });
  }

  applyBatchStatus(): void {
    if (this.isBatchUpdating()) return;

    const targetStatus = this.eventStatuses().find(status => status.id === this.batchStatusId());
    if (!targetStatus || !isManuallySelectableCalendarEventStatus(targetStatus.name)) {
      this.batchStatusId.set(null);
      this.toastr.warning('Selecciona un estado disponible para las tareas.', 'Calendario');
      return;
    }

    const selectedEvents = this.getSelectedCalendarEvents();
    const identities = selectedEvents
      .map(event => this.getEventSelectionIdentity(event))
      .filter((identity): identity is CalendarEventSelectionIdentity => Boolean(identity));

    if (selectedEvents.length !== this.selectedEventIds().size
      || identities.length !== selectedEvents.length
      || !isValidCalendarEventSelection(identities)) {
      this.toastr.error(
        'La selección ya no es válida. Las tareas deben ser del mismo condominio, día y tipo.',
        'Calendario'
      );
      this.clearBatchSelection();
      return;
    }

    const hasLockedEvent = selectedEvents.some(event =>
      isTerminalCalendarEventStatus(
        (event.extendedProps['eventStatus'] as CalendarEventStatus | undefined)?.name
      )
    );
    if (hasLockedEvent) {
      this.toastr.error('La selección contiene una tarea con estado definitivo.', 'Calendario');
      this.clearBatchSelection();
      return;
    }

    const eventIds = identities.map(identity => identity.eventId);
    const eventsById = new Map(
      selectedEvents.map(event => [Number(event.extendedProps['calendarEventId']), event])
    );
    const failedEventIds = new Set<number>();
    const updatedStatusesById = new Map<number, CalendarEventStatus>();
    let successfulUpdates = 0;

    this.batchCompletedCount.set(0);
    this.batchTotalCount.set(eventIds.length);
    this.isBatchUpdating.set(true);
    this.updatingEventIds.set(new Set(eventIds));
    this.rerenderCalendarEvents();

    this.batchUpdateSubscription?.unsubscribe();
    this.batchUpdateSubscription = this.store
      .updateCalendarEventStatuses(eventIds, targetStatus.id)
      .subscribe({
        next: result => {
          this.batchCompletedCount.update(count => count + 1);
          if (!result.success) {
            failedEventIds.add(result.eventId);
            return;
          }

          successfulUpdates += 1;
          updatedStatusesById.set(result.eventId, {
            ...targetStatus,
            ...(result.event?.event_status ?? {})
          });
        },
        complete: () => {
          const calendarApi = this.calendar.getApi();
          calendarApi.batchRendering(() => {
            updatedStatusesById.forEach((updatedStatus, eventId) => {
              eventsById.get(eventId)?.setExtendedProp('eventStatus', updatedStatus);
            });
          });

          this.finishBatchUpdate(failedEventIds, successfulUpdates);
        },
        error: () => {
          this.updatingEventIds.set(new Set<number>());
          this.isBatchUpdating.set(false);
          this.rerenderCalendarEvents();
          this.toastr.error('No fue posible actualizar las tareas seleccionadas.', 'Calendario');
        }
      });
  }

  clearBatchSelection(rerender = true): void {
    if (this.isBatchUpdating()) return;

    this.selectedEventIds.set(new Set<number>());
    this.selectionAnchor.set(null);
    this.batchStatusId.set(null);
    if (rerender) {
      this.rerenderCalendarEvents();
    }
  }

  selectAllSelectionGroup(): void {
    if (this.isBatchUpdating()) return;

    const group = this.selectionGroup();
    if (!group || group.allSelected) return;

    this.selectedEventIds.set(new Set(group.eventIds));
    this.rerenderCalendarEvents();
  }

  private toggleEventSelection(
    identity: CalendarEventSelectionIdentity,
    selected: boolean
  ): void {
    const anchor = this.selectionAnchor();
    if (selected && anchor) {
      const mismatch = getCalendarEventSelectionMismatch(anchor, identity);
      if (mismatch) {
        this.toastr.warning(this.getSelectionMismatchMessage(mismatch), 'Calendario');
        this.rerenderCalendarEvents();
        return;
      }
    }

    const nextIds = new Set(this.selectedEventIds());
    selected ? nextIds.add(identity.eventId) : nextIds.delete(identity.eventId);
    this.selectedEventIds.set(nextIds);

    if (nextIds.size === 0) {
      this.selectionAnchor.set(null);
      this.batchStatusId.set(null);
    } else if (!anchor || !nextIds.has(anchor.eventId)) {
      this.syncSelectionAnchor(nextIds);
    }

    this.rerenderCalendarEvents();
  }

  private getEventSelectionIdentity(
    calendarEvent: EventApi
  ): CalendarEventSelectionIdentity | null {
    const eventId = Number(calendarEvent.extendedProps['calendarEventId']);
    const settlementId = Number(calendarEvent.extendedProps['settlementId']);
    const eventTypeId = Number(calendarEvent.extendedProps['eventTypeId']);
    const eventDate = String(calendarEvent.extendedProps['eventDate'] ?? '').split('T')[0];

    if (!Number.isFinite(eventId)
      || !Number.isFinite(settlementId)
      || !Number.isFinite(eventTypeId)
      || !eventDate) {
      return null;
    }

    return { eventId, settlementId, eventDate, eventTypeId };
  }

  private getEventSelectionCandidate(
    calendarEvent: EventApi
  ): CalendarEventSelectionCandidate | null {
    const identity = this.getEventSelectionIdentity(calendarEvent);
    if (!identity) return null;

    const currentStatus = calendarEvent.extendedProps['eventStatus'] as CalendarEventStatus | undefined;
    const rawUnitId = Number(calendarEvent.extendedProps['unitId']);
    const isUpdating = this.updatingEventIds().has(identity.eventId);
    const remainsSelectableDuringBatch = this.isBatchUpdating()
      && this.selectedEventIds().has(identity.eventId);
    const selectable = Boolean(currentStatus)
      && this.eventStatuses().length === CALENDAR_EVENT_STATUS_NAMES.length
      && !isTerminalCalendarEventStatus(currentStatus?.name)
      && (!isUpdating || remainsSelectableDuringBatch);

    return {
      ...identity,
      settlementName: String(calendarEvent.extendedProps['settlementName'] ?? '').trim(),
      unitId: Number.isFinite(rawUnitId) ? rawUnitId : null,
      selectable
    };
  }

  private getEventSelectionDisabledReason(
    identity: CalendarEventSelectionIdentity | null,
    currentStatus: CalendarEventStatus | undefined
  ): string | null {
    if (!identity) {
      return 'La tarea no contiene la información necesaria para seleccionarla';
    }
    if (this.isBatchUpdating() || this.updatingEventIds().has(identity.eventId)) {
      return 'La tarea se está actualizando';
    }
    if (!currentStatus
      || this.eventStatuses().length !== CALENDAR_EVENT_STATUS_NAMES.length) {
      return 'El catálogo de estados no está disponible';
    }
    if (isTerminalCalendarEventStatus(currentStatus?.name)) {
      return 'La tarea tiene un estado definitivo';
    }

    const anchor = this.selectionAnchor();
    if (!anchor || this.selectedEventIds().has(identity.eventId)) {
      return null;
    }

    const mismatch = getCalendarEventSelectionMismatch(anchor, identity);
    return mismatch ? this.getSelectionMismatchMessage(mismatch) : null;
  }

  private getSelectionMismatchMessage(mismatch: CalendarEventSelectionMismatch): string {
    const messages: Record<CalendarEventSelectionMismatch, string> = {
      settlement: 'Sólo puedes seleccionar tareas del mismo condominio.',
      date: 'Sólo puedes seleccionar tareas del mismo día.',
      eventType: 'Sólo puedes seleccionar tareas del mismo tipo.'
    };
    return messages[mismatch];
  }

  private getSelectedCalendarEvents(): EventApi[] {
    const selectedIds = this.selectedEventIds();
    return this.calendar.getApi().getEvents().filter(event =>
      selectedIds.has(Number(event.extendedProps['calendarEventId']))
    );
  }

  private syncSelectionAnchor(selectedIds: ReadonlySet<number>): void {
    const nextAnchor = this.calendar.getApi().getEvents()
      .map(event => this.getEventSelectionIdentity(event))
      .find((identity): identity is CalendarEventSelectionIdentity =>
        identity !== null && selectedIds.has(identity.eventId)
      ) ?? null;
    this.selectionAnchor.set(nextAnchor);
  }

  private finishBatchUpdate(
    failedEventIds: Set<number>,
    successfulUpdates: number
  ): void {
    this.updatingEventIds.set(new Set<number>());
    this.isBatchUpdating.set(false);
    this.selectedEventIds.set(failedEventIds);

    if (failedEventIds.size === 0) {
      this.selectionAnchor.set(null);
      this.batchStatusId.set(null);
      this.toastr.success(
        `Se actualizaron ${successfulUpdates} ${successfulUpdates === 1 ? 'tarea' : 'tareas'}.`,
        'Calendario'
      );
    } else {
      this.syncSelectionAnchor(failedEventIds);
      if (successfulUpdates > 0) {
        this.toastr.warning(
          `${successfulUpdates} actualizadas y ${failedEventIds.size} con error.`,
          'Calendario'
        );
      } else {
        this.toastr.error('No fue posible actualizar las tareas seleccionadas.', 'Calendario');
      }
    }

    this.rerenderCalendarEvents();
    this.calendar.getApi().refetchEvents();
  }

  private rerenderCalendarEvents(): void {
    const calendarApi = this.calendar?.getApi();
    if (!calendarApi) return;

    const eventsById = new Map(
      calendarApi.getEvents().map(event => [
        Number(event.extendedProps['calendarEventId']),
        event
      ])
    );
    const selectedIds = this.selectedEventIds();
    const root = this.hostElement.nativeElement;

    root.querySelectorAll<HTMLInputElement>('.event-selection-checkbox').forEach(checkbox => {
      const eventId = Number(checkbox.dataset['calendarEventId']);
      const calendarEvent = eventsById.get(eventId);
      if (!calendarEvent) return;

      const identity = this.getEventSelectionIdentity(calendarEvent);
      const currentStatus = calendarEvent.extendedProps['eventStatus'] as CalendarEventStatus | undefined;
      const disabledReason = this.getEventSelectionDisabledReason(identity, currentStatus);

      checkbox.checked = selectedIds.has(eventId);
      checkbox.disabled = disabledReason !== null;

      const control = checkbox.closest<HTMLLabelElement>('.event-selection-control');
      if (control) {
        control.title = disabledReason ?? 'Seleccionar tarea para actualización por lote';
      }
    });

    root.querySelectorAll<HTMLSelectElement>('.event-status-select').forEach(select => {
      const eventId = Number(select.dataset['calendarEventId']);
      const calendarEvent = eventsById.get(eventId);
      if (!calendarEvent) return;

      const currentStatus = calendarEvent.extendedProps['eventStatus'] as CalendarEventStatus | undefined;
      if (selectedIds.has(eventId)) {
        select.dataset['interactionState'] = 'unavailable';
        select.disabled = true;
        select.title = 'La tarea está seleccionada para actualización por lote';
        return;
      }

      const interactionState = getCalendarEventStatusInteractionState({
        statusName: currentStatus?.name,
        hasCurrentStatus: Boolean(currentStatus),
        hasValidEventId: Number.isFinite(eventId),
        hasCompleteCatalog: this.eventStatuses().length === CALENDAR_EVENT_STATUS_NAMES.length,
        isUpdating: this.updatingEventIds().has(eventId)
      });
      this.applyStatusAppearance(select, currentStatus?.name);
      this.applyStatusInteractionState(select, interactionState);
    });
  }

  private setEventUpdating(calendarEventId: number, isUpdating: boolean) {
    this.updatingEventIds.update(currentIds => {
      const nextIds = new Set(currentIds);
      isUpdating ? nextIds.add(calendarEventId) : nextIds.delete(calendarEventId);
      return nextIds;
    });
  }

  private applyStatusAppearance(select: HTMLSelectElement, statusName?: CalendarEventStatusName) {
    select.dataset['status'] = statusName?.toLowerCase().replace('_', '-') ?? 'unknown';
  }

  private applyStatusInteractionState(
    select: HTMLSelectElement,
    interactionState: CalendarEventStatusInteractionState
  ) {
    select.dataset['interactionState'] = interactionState;
    select.disabled = interactionState !== 'editable';

    const titles: Record<CalendarEventStatusInteractionState, string> = {
      editable: 'Cambiar estado del evento',
      locked: 'Estado definitivo: ya no se puede modificar',
      updating: 'Guardando cambio de estado',
      unavailable: 'Cambio de estado no disponible'
    };
    select.title = titles[interactionState];
  }

  private buildCalendarParams(
    startDate: string,
    endDate: string,
    utilityId = this.getActiveUtilityId()
  ): Partial<CalendarParams> {
    const params: Partial<CalendarParams> = {
      start_date: startDate,
      end_date: endDate,
      utility_id: utilityId ?? undefined
    };

    if (this.eventType()) params.event_id = this.eventType()!;
    if ([0, 1].includes(this.settlementType()!)) params.settlement_tipo = this.settlementType()!;
    if (this.selectedSettlement()) params.settlement_id = Number(this.selectedSettlement().id);
    if (this.unitStatus()) params.unit_status = this.unitStatus()!;
    if (this.municipality().trim()) params.municipality = this.municipality().trim();
    return params;
  }

  private getActiveUtilityId(): number | null {
    const utilityId = Number(this.currentProfile()?.utility_id);
    return Number.isFinite(utilityId) && utilityId > 0 ? utilityId : null;
  }

  private refreshForProfile(profile: any): void {
    this.batchUpdateSubscription?.unsubscribe();
    this.isBatchUpdating.set(false);
    this.batchCompletedCount.set(0);
    this.batchTotalCount.set(0);
    this.updatingEventIds.set(new Set<number>());
    this.clearBatchSelection(false);
    this.profile = profile ?? {};
    this.resetFilters();
    this.settlementSelect?.close();
    this.settlementSelect?.reset();

    const calendarApi = this.calendar?.getApi();
    if (!calendarApi) return;

    calendarApi.refetchEvents();
  }

  private buildSettlementLabels(event: any) {
    const settlementName = (event.settlement?.name ?? '').trim();
    const unitName = (event.unit_name ?? '').trim();
    const description = (event.description ?? '').trim();
    const unitDescription = [unitName, description].filter(Boolean).join(' - ');
    const hasSettlementName = Boolean(settlementName);
    const primaryLabel = hasSettlementName ? settlementName : unitDescription;
    const secondaryLabel = hasSettlementName ? unitDescription : '';
    const titleParts = [settlementName, unitName, description];
    const title = titleParts.filter(Boolean).join(' - ') || unitDescription;
    return {
      title,
      primaryLabel,
      secondaryLabel,
      settlementLabel: settlementName,
      unitLabel: unitName,
      eventTypeLabel: description
    };
  }

  isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  applyFilters() {
    if (this.isBatchUpdating()) return;
    this.clearBatchSelection(false);
    const api = this.calendar.getApi();
    api.refetchEvents();
  }

  clearFilters() {
    this.resetFilters();
    this.applyFilters();
  }

  private resetFilters(): void {
    this.eventType.set(null);
    this.settlementType.set(null);
    this.unitStatus.set(null);
    this.selectedSettlement.set(null);
    this.municipality.set('');
  }
}
